import { Cookies } from "@garimpa/lib/cookies";
import { getSafeRedirectPath } from "@garimpa/lib/getSafeRedirectPath";
import { readCookie } from "@garimpa/lib/readCookie";
import { getSearchSerialization } from "@garimpa/lib/searchSerialization";
import { serializeCookie } from "@garimpa/lib/serializeCookie";
import { serializeExpiredCookie } from "@garimpa/lib/serializeExpiredCookie";

import { ACCOUNT_CREATION_CALLBACK_PARAM, TELEGRAM_CONNECTION_CALLBACK_PARAM } from "./analytics";

const FALLBACK_REDIRECT_PATH = "/";
const { stringifySearch } = getSearchSerialization();

export type LoginMode = "signin" | "signup";

export interface LoginRedirectLocation {
  href?: string;
  pathname?: string;
  searchStr?: string;
  hash?: string;
}

interface LoginRedirectRouter {
  navigate: (options: { href: string }) => unknown;
}

export function getRedirectPathFromLocation(
  location: LoginRedirectLocation | null | undefined,
): string | undefined {
  if (!location) return undefined;

  const safeHref = getSafeRedirectPath(location.href);
  if (safeHref) return safeHref;

  const pathname = location.pathname;
  if (!pathname) return undefined;

  const hash = location.hash ? `${location.hash.startsWith("#") ? "" : "#"}${location.hash}` : "";
  return getSafeRedirectPath(`${pathname}${location.searchStr ?? ""}${hash}`);
}

export function getCurrentBrowserRedirectPath(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return getSafeRedirectPath(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
}

function buildHrefWithRedirect(
  basePath: string,
  path: string | null | undefined,
  extraSearch?: Record<string, string | undefined>,
): string {
  const safePath = getSafeRedirectPath(path);
  const search = {
    ...extraSearch,
    redirect: safePath,
  };

  const queryString = stringifySearch(search);
  if (!queryString) return basePath;

  // Encode through the router codec so the login route's `pickString(raw.redirect)`
  // reads back the exact path, query and hash.
  return `${basePath}${queryString}`;
}

export function buildLoginHrefWithRedirect(
  path: string | null | undefined,
  options: { mode?: LoginMode } = {},
): string {
  return buildHrefWithRedirect("/login", path, { mode: options.mode });
}

export function buildSignupHrefWithRedirect(path: string | null | undefined): string {
  return buildLoginHrefWithRedirect(path, { mode: "signup" });
}

export function buildOnboardingHrefWithRedirect(path: string | null | undefined): string {
  return buildHrefWithRedirect("/onboarding", path);
}

export function buildGoogleSignupOnboardingHrefWithRedirect(
  path: string | null | undefined,
): string {
  return buildHrefWithRedirect("/onboarding", path, {
    [ACCOUNT_CREATION_CALLBACK_PARAM]: "google",
  });
}

export function buildTelegramSignupOnboardingHrefWithRedirect(
  path: string | null | undefined,
): string {
  return buildHrefWithRedirect("/onboarding", path, {
    [ACCOUNT_CREATION_CALLBACK_PARAM]: "telegram",
  });
}

export function buildTelegramConnectionCallbackHref(path: string | null | undefined): string {
  const safePath = getSafeRedirectPath(path) ?? FALLBACK_REDIRECT_PATH;
  const url = new URL(safePath, "https://garimpa.club");
  url.searchParams.set(TELEGRAM_CONNECTION_CALLBACK_PARAM, "true");
  return `${url.pathname}${url.search}${url.hash}`;
}

function getRedirectCookiePath() {
  return readCookie(document.cookie, Cookies.redirectAfterLogin) ?? undefined;
}

function clearRedirectCookie() {
  document.cookie = serializeExpiredCookie(Cookies.redirectAfterLogin, {
    secure: window.location.protocol === "https:",
  });
}

/**
 * Saves the current path so the user can be redirected back after login.
 * Uses a short-lived cookie that survives OAuth redirects (unlike URL params).
 */
export function setRedirectAfterLogin(path: string) {
  const safePath = getSafeRedirectPath(path);
  if (!safePath) return;
  document.cookie = serializeCookie(Cookies.redirectAfterLogin, safePath, {
    secure: window.location.protocol === "https:",
  });
}

export function navigateToLoginAfterCurrentPage(router: LoginRedirectRouter) {
  const redirectPath = getCurrentBrowserRedirectPath();
  if (redirectPath) setRedirectAfterLogin(redirectPath);
  return router.navigate({ href: buildLoginHrefWithRedirect(redirectPath) });
}

/**
 * Reads and clears the redirect-after-login cookie.
 * Returns a safe URL search redirect first, then the stored cookie path, or "/".
 */
export function getRedirectAfterLogin(searchRedirect?: string | null): string {
  const cookiePath = getRedirectCookiePath();
  clearRedirectCookie();

  return (
    getSafeRedirectPath(searchRedirect) ?? getSafeRedirectPath(cookiePath) ?? FALLBACK_REDIRECT_PATH
  );
}
